import {
  isUnrestrictedHierarchyRole,
  normalizeHierarchyRoleCode,
} from "./inboxReviewScope";

export const EMPTY_INBOX_SUMMARY = {
  pending: 0,
  assigned: 0,
  unassigned: 0,
  older_than_24h: 0,
  verified: 0,
  verified_today: 0,
  avg_verify_minutes: null,
  my_pending: 0,
  my_older_than_24h: 0,
  team_pending: 0,
  my_verified: 0,
  team_verified: 0,
  department_count: 0,
  member_count: 0,
  departments: [],
  people: [],
};

export function inboxKpiLayout(roleCode, isAdmin = false) {
  if (isUnrestrictedHierarchyRole(roleCode, isAdmin)) {
    return "org";
  }

  const code = normalizeHierarchyRoleCode(roleCode);
  if (code === "SENIOR_MANAGER") {
    return "senior";
  }
  if (code === "MANAGER") {
    return "manager";
  }
  return "member";
}

export function formatInboxCount(value) {
  return Number(value || 0).toLocaleString();
}

export function formatAvgVerifyDuration(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) {
    return "—";
  }
  const n = Math.round(Number(minutes));
  if (n < 60) {
    return `${n} min`;
  }
  const hrs = Math.floor(n / 60);
  const mins = n % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

export function buildPendingActionItems({
  summary = EMPTY_INBOX_SUMMARY,
  layout,
} = {}) {
  if (layout === "org") {
    return [
      {
        key: "attention",
        label: "Needs attention",
        value: formatInboxCount(summary.older_than_24h),
        hint: "Older than 24 hours",
        assigned_state: "all",
        older_than_24h: true,
      },
      {
        key: "delay",
        label: "At risk of delay",
        value: formatInboxCount(summary.unassigned),
        hint: "Unassigned",
        assigned_state: "unassigned",
        older_than_24h: false,
      },
      {
        key: "mine",
        label: "Assigned to you",
        value: formatInboxCount(summary.my_pending),
        hint: "Your queue",
        assigned_state: "mine",
        older_than_24h: false,
      },
      {
        key: "teams",
        label: "Assigned to teams",
        value: formatInboxCount(summary.assigned),
        hint: "In progress",
        assigned_state: "assigned",
        older_than_24h: false,
      },
    ];
  }

  if (layout === "senior") {
    return [
      {
        key: "attention",
        label: "Needs attention",
        value: formatInboxCount(summary.older_than_24h),
        hint: "Older than 24 hours",
        assigned_state: "all",
        older_than_24h: true,
      },
    ];
  }

  return [];
}

function percentOf(part, whole) {
  if (!whole) {
    return "0% of total";
  }

  return `${Math.round((Number(part) / Number(whole)) * 100)}% of total`;
}

export function buildInboxKpiCards({
  summary = EMPTY_INBOX_SUMMARY,
  roleCode,
  isAdmin = false,
  tab = "pending",
} = {}) {
  const layout = inboxKpiLayout(roleCode, isAdmin);
  const pendingTab = tab !== "verified";

  if (!pendingTab) {
    if (layout === "org") {
      return [
        {
          key: "verified",
          value: formatInboxCount(summary.verified),
          label: "Verified this period",
          hint: "This period",
        },
        {
          key: "verified_today",
          value: formatInboxCount(summary.verified_today),
          label: "Verified today",
          hint: "Today",
        },
      ];
    }

    if (layout === "senior") {
      const named = (summary.departments || []).filter(
        (dept) => dept.department_id !== "unassigned",
      );
      return [
        {
          key: "verified",
          value: formatInboxCount(summary.verified),
          label: "Total verified",
          hint: "Across departments",
        },
        ...named.map((dept) => ({
          key: `dept_verified_${dept.department_id}`,
          value: formatInboxCount(dept.verified_count),
          label: `${dept.department_name} verified`,
          hint: "This period",
          tone: "assigned",
        })),
        {
          key: "verified_today",
          value: formatInboxCount(summary.verified_today),
          label: "Verified today",
          hint: "Today",
        },
      ];
    }

    if (layout === "manager") {
      return [
        {
          key: "my_verified",
          value: formatInboxCount(summary.my_verified),
          label: "Verified by me",
          hint: "My submissions",
        },
        {
          key: "team_verified",
          value: formatInboxCount(summary.team_verified),
          label: "Verified by team",
          hint: "Team submissions",
        },
        {
          key: "verified",
          value: formatInboxCount(summary.verified),
          label: "Total verified",
          hint: "This period",
        },
        {
          key: "period",
          value: new Date().toLocaleString("en-US", {
            month: "short",
            year: "numeric",
          }),
          label: "Verification period",
          hint: "Current period",
        },
      ];
    }

    return [
      {
        key: "my_verified",
        value: formatInboxCount(summary.my_verified ?? summary.verified),
        label: "My verified",
        hint: "This period",
      },
        {
          key: "verified_today",
          value: formatInboxCount(summary.verified_today),
          label: "Verified today",
          hint: "Today",
        },
      ];
  }

  if (layout === "org") {
    return [
      {
        key: "pending",
        value: formatInboxCount(summary.pending),
        label: "Pending review",
        hint: "In queue",
      },
      {
        key: "unassigned",
        value: formatInboxCount(summary.unassigned),
        label: "Unassigned",
        hint: percentOf(summary.unassigned, summary.pending),
      },
        {
          key: "assigned",
          value: formatInboxCount(summary.assigned),
          label: "Assigned",
          hint: percentOf(summary.assigned, summary.pending),
        },
        {
          key: "older",
        value: formatInboxCount(summary.older_than_24h),
        label: "Older than 24 hours",
        hint: "At risk of delay",
      },
    ];
  }

  if (layout === "senior") {
    return [
      {
        key: "pending",
        value: formatInboxCount(summary.pending),
        label: "Total pending",
        hint: "Across departments",
      },
      {
        key: "assigned",
        value: formatInboxCount(summary.assigned),
        label: "Assigned",
        hint: percentOf(summary.assigned, summary.pending),
      },
      {
        key: "older",
        value: formatInboxCount(summary.older_than_24h),
        label: "Older than 24h",
        hint: "At risk",
      },
    ];
  }

  if (layout === "manager") {
    return [
      {
        key: "my_pending",
        value: formatInboxCount(summary.my_pending),
        label: "My pending",
        hint: "Assigned to you",
      },
      {
        key: "my_older",
        value: formatInboxCount(summary.my_older_than_24h),
        label: "My older than 24h",
        hint: "Needs immediate action",
      },
      {
        key: "team_pending",
        value: formatInboxCount(summary.team_pending),
        label: "Team pending",
        hint: "Your team's queue",
      },
    ];
  }

  const inProgress = Math.max(
    0,
    (Number(summary.my_pending) || 0) - (Number(summary.my_older_than_24h) || 0),
  );

  return [
    {
      key: "my_pending",
      value: formatInboxCount(summary.my_pending),
      label: "My pending",
      hint: "Items to review",
    },
    {
      key: "older",
      value: formatInboxCount(summary.older_than_24h),
      label: "Older than 24h",
      hint: "Review urgently",
    },
    {
      key: "in_progress",
      value: formatInboxCount(inProgress),
      label: "In progress",
      hint: "Currently reviewing",
    },
  ];
}
